/**
 * CourseCanvas Component
 * Shared content canvas for Authoring Preview and exported course packages.
 *
 * This component handles:
 * - Rendering blocks in sequence
 * - Scroll-triggered reveal for block content
 * - Content blocking (hiding content after incomplete required interactions)
 * - Completion tracking integration
 *
 * Required blocks are gated by shared completion logic, while each renderer
 * can choose its own affordance (for example, the spectrum and likert blocks
 * use their submit buttons instead of the shared inline interaction message).
 */

import React, { useRef, useCallback } from 'react';
import type { MediaPlayerInstance } from '@vidstack/react';
import type {
  CategorizationBlock,
  CourseBlock,
  ElectronicSignatureBlock,
  LessonThemeColor,
  LinkBlock,
  PolicyAcknowledgementBlock,
  SuccessAnimationType,
} from '@course-authoring/shared-types';
import {
  blockRequiresCompletion,
  getEffectiveBlockCompletionMode,
  isLessonCompleteBlock,
  useTheme,
  useAccountConfig,
  resolveLessonThemeColor,
} from '@course-authoring/shared-types';
import {
  correctCategorizationAssignments,
  fingerprintAcknowledgementContent,
  fingerprintCategorizationAnswer,
} from './contentFingerprint';
import { AudioControls } from './AudioControls';
import { useAudioControls } from './AudioControlsContext';
import { CourseSectionShell } from './CourseSectionShell';
import { formatBlockId } from './formatBlockId';
import { InteractionMessageCompletionProvider } from './InteractionMessage';
import { LessonTitleHeader } from './LessonTitleHeader';
import { renderCourseCanvasBlock, type BlockResultsDistributions } from './courseCanvasBlockRegistry';
import { useBlockReveal } from './hooks/useBlockReveal';
import { isVisibleIntersectionUncovered } from './intersectionVisibility';
import { measureStickyTopBandHeight, NARRATION_SCROLL_START_EVENT } from './narrationScroll';
import { useMeasuredHeight } from './hooks/useMeasuredHeight';
import { prefersReducedMotion, usePrefersReducedMotion } from './hooks/usePrefersReducedMotion';
import { pacedBlockCount, type BlockPacing } from './blockPacing';
import { useNarrationPacing } from './hooks/useNarrationPacing';
import { attachManualIntentListeners, isClickExpandTopicControlOf } from './narrationManualIntent';
import { PacingBoundary } from './PacingBoundary';
import { useScrollCompletion } from './hooks/useScrollCompletion';
import type { VideoTranscriptOperations } from './VideoRenderer';
import type { ClickExpandTargetRegistration, NarrationEventHandler } from './narrationEvents';

const emptyPacingSubscribe = (): (() => void) => () => undefined;
const emptyPacingSnapshot = (): number => 0;

function cssEscape(value: string): string {
  if (typeof CSS !== 'undefined' && typeof CSS.escape === 'function') {
    return CSS.escape(value);
  }
  // Conservative fallback for non-DOM environments (tests).
  return value.replace(/(["\\])/g, '\\$1');
}

interface ConfettiLauncher {
  (options?: Record<string, unknown>): Promise<unknown> | null;
  shapeFromPath?: (options: {
    path: string;
    matrix?: number[];
  }) => unknown;
  shapeFromText?: (options: {
    text: string;
    scalar?: number;
    color?: string;
    fontFamily?: string;
  }) => unknown;
}

let confettiLoaderPromise: Promise<ConfettiLauncher> | null = null;
const SUCCESS_ANIMATION_PARTICLE_COUNT = 250;
const SUCCESS_ANIMATION_Z_INDEX = 10000;
const CONFETTI_LAUNCH_STAGGER_MS = 120;
const GOLD_COIN_RAIN_LAUNCH_STAGGER_MS = 10;
const FLASHY_GOLD_COIN_PATH =
  'M12 0.5L15.2 1.7L18.8 3.8L21 7L22.2 10.1L23.5 12L22.2 13.9L21 17L18.8 20.2L15.2 22.3L12 23.5L8.8 22.3L5.2 20.2L3 17L1.8 13.9L0.5 12L1.8 10.1L3 7L5.2 3.8L8.8 1.7Z';

function loadConfettiLauncher(): Promise<ConfettiLauncher> {
  if (confettiLoaderPromise) {
    return confettiLoaderPromise;
  }

  confettiLoaderPromise = import('canvas-confetti').then((module) => {
    const maybeDefault = (module as { default?: unknown }).default;
    if (typeof maybeDefault !== 'function') {
      throw new Error('canvas-confetti default export is not available');
    }
    return maybeDefault as ConfettiLauncher;
  });

  return confettiLoaderPromise;
}

function resolveSuccessAnimationType(block: CourseBlock): SuccessAnimationType | null {
  if (block.type !== 'basic-content-a' && block.type !== 'basic-content-b' && block.type !== 'lesson-complete') {
    return null;
  }

  return block.successAnimationType ?? null;
}

function launchConfettiEffect(confetti: ConfettiLauncher): void {
  const confettiLaunches = [
    {
      angle: 55,
      origin: { x: 0.08, y: 0.78 },
      particleCount: 44,
      spread: 79,
      startVelocity: 50,
    },
    {
      angle: 125,
      origin: { x: 0.92, y: 0.78 },
      particleCount: 50,
      spread: 80,
      startVelocity: 60,
    },
    {
      angle: 72,
      origin: { x: 0.32, y: 0.7 },
      particleCount: 37,
      spread: 72,
      startVelocity: 56,
    },
    {
      angle: 108,
      origin: { x: 0.68, y: 0.7 },
      particleCount: 42,
      spread: 72,
      startVelocity: 56,
    },
    {
      angle: 90,
      origin: { x: 0.5, y: 0.66 },
      particleCount: 150,
      spread: 100,
      startVelocity: 56,
    },
  ] as const;
  const baseOptions: Record<string, unknown> = {
    ticks: 150,
    gravity: 1.1,
    scalar: 1,
    zIndex: SUCCESS_ANIMATION_Z_INDEX,
    disableForReducedMotion: true,
    colors: ['#2563EB', '#F7007B', '#F59E0B', '#22C55E', '#FFFFFF'],
  };

  confettiLaunches.forEach((launch, index) => {
    const fireLaunch = () => {
      void confetti({
        ...baseOptions,
        particleCount: launch.particleCount,
        angle: launch.angle,
        spread: launch.spread,
        startVelocity: launch.startVelocity,
        origin: launch.origin,
      });
    };

    if (index === 0) {
      fireLaunch();
      return;
    }

    window.setTimeout(fireLaunch, index * CONFETTI_LAUNCH_STAGGER_MS);
  });
}

function launchPixieDustEffect(confetti: ConfettiLauncher): void {
  const centerBurstParticleCount = 420;
  const sideBurstParticleCount = (SUCCESS_ANIMATION_PARTICLE_COUNT - centerBurstParticleCount) / 2;
  const baseOptions: Record<string, unknown> = {
    spread: 160,
    startVelocity: 42,
    ticks: 240,
    gravity: 0.2,
    decay: 0.94,
    scalar: 0.72,
    drift: 0,
    zIndex: SUCCESS_ANIMATION_Z_INDEX,
    disableForReducedMotion: true,
    colors: ['#FFFFFF', '#F5D0FE', '#DDD6FE', '#BFDBFE', '#FDE68A'],
    shapes: ['star', 'circle'],
  };

  void confetti({
    ...baseOptions,
    particleCount: centerBurstParticleCount,
    angle: 90,
    origin: { x: 0.5, y: 1 },
  });
  void confetti({
    ...baseOptions,
    particleCount: sideBurstParticleCount,
    angle: 65,
    origin: { x: 0.2, y: 0.98 },
  });
  void confetti({
    ...baseOptions,
    particleCount: sideBurstParticleCount,
    angle: 115,
    origin: { x: 0.8, y: 0.98 },
  });
}

function launchGoldCoinsEffect(confetti: ConfettiLauncher): void {
  const rainOrigins = [0.05, 0.12, 0.19, 0.26, 0.33, 0.40, 0.47, 0.53, 0.60, 0.67, 0.74, 0.81, 0.88, 0.95] as const;
  const rainOriginJitterOffsets = [-0.02, 0.02, -0.01, 0.01, -0.015, 0.015, -0.02, 0.02, -0.01, 0.01, -0.015, 0.015, -0.02, 0.02] as const;
  const baseRainBurstParticleCount = Math.floor(
    SUCCESS_ANIMATION_PARTICLE_COUNT / rainOrigins.length
  );
  const rainBurstRemainder = SUCCESS_ANIMATION_PARTICLE_COUNT % rainOrigins.length;
  const coinShape =
    confetti.shapeFromPath?.({ path: FLASHY_GOLD_COIN_PATH }) ??
    confetti.shapeFromText?.({ text: '🪙', scalar: 1.2 });
  const baseOptions: Record<string, unknown> = {
    angle: 270,
    spread: 180,
    startVelocity: 10,
    ticks: 240,
    gravity: 1,
    drift: 0,
    scalar: 1.2,
    flat: true,
    zIndex: SUCCESS_ANIMATION_Z_INDEX,
    disableForReducedMotion: true,
    colors: ['#FFF4B3', '#FFE082', '#FFD54A', '#FFC107', '#FFB300'],
    shapes: coinShape ? [coinShape] : ['circle'],
  };

  // Create dispersed start times across a range (0-200ms spread)
  const timeSpread = 200;
  const timeOffsets = rainOrigins.map((_, index) => 
    Math.floor((index / (rainOrigins.length - 1)) * timeSpread)
  );
  // Shuffle for more natural dispersion
  const shuffledTimeOffsets = [...timeOffsets].sort(() => Math.random() - 0.5);

  rainOrigins.forEach((x, index) => {
    const jitterOffset = rainOriginJitterOffsets[index] ?? 0;
    const jitteredOriginX = Number(
      Math.min(0.98, Math.max(0.02, x + jitterOffset)).toFixed(3)
    );
    // Random start velocity between 10-25
    const randomStartVelocity = 10 + Math.random() * 15;
    const launchRainBurst = () => {
      void confetti({
        ...baseOptions,
        startVelocity: randomStartVelocity,
        particleCount:
          baseRainBurstParticleCount + (index < rainBurstRemainder ? 1 : 0),
        origin: { x: jitteredOriginX, y: 0 },
      });
    };

    const timeOffset = shuffledTimeOffsets[index] ?? 0;
    window.setTimeout(launchRainBurst, timeOffset);
  });
}

function triggerSuccessAnimation(type: SuccessAnimationType): void {
  if (typeof window === 'undefined') {
    return;
  }

  if (prefersReducedMotion()) {
    return;
  }

  void loadConfettiLauncher()
    .then((confetti) => {
      if (type === 'pixie-dust') {
        launchPixieDustEffect(confetti);
        return;
      }

      if (type === 'gold-coins') {
        launchGoldCoinsEffect(confetti);
        return;
      }

      launchConfettiEffect(confetti);
    })
    .catch(() => {
      // If confetti fails to load, avoid interrupting lesson flow.
    });
}

export interface CourseCanvasProps {
  pacing?: BlockPacing;
  /** List of blocks to render */
  blocks: CourseBlock[];
  /** Map of block IDs to completion status */
  completionStatus: Record<string, boolean>;
  /** Callback when a block is completed */
  onBlockComplete: (blockId: string) => void;
  /**
   * Callback when a learner submits an answer on a loggable question block
   * (Single Choice first submit, Likert/WCS terminal submit). Only the export
   * runtime wires this — authoring-hosted canvases (editor and previews)
   * leave it undefined so authors never log responses (NA-178).
   */
  onQuestionAnswer?: (blockId: string, answer: { optionId: string }) => void;
  /**
   * Callback when a learner submits a Freeform Text response. Separately
   * typed from `onQuestionAnswer` (text, not an option id) — same pattern as
   * `onCategorizationAnswerSubmit` below for a structurally different answer
   * shape. Only the export runtime wires this (NA-444).
   */
  onFreeformTextAnswerSubmit?: (blockId: string, answer: { text: string }) => void;
  /**
   * Real aggregated results per block id, for Likert/WCS blocks only (NA-181).
   * Populated by the export runtime after a learner submits an answer; absent
   * (or missing a given block id) falls back to each renderer's own placeholder.
   */
  resultsByBlockId?: Record<string, BlockResultsDistributions>;
  /**
   * Per-block prior-answer status/value (NA-319), for Likert/Single
   * Choice/WCS blocks only. Populated by the export runtime from a
   * bootstrap-time bulk fetch of the learner's prior answers; absent (or
   * missing a given block id) falls back to each renderer's own
   * `'unanswered'` default, e.g. authoring-hosted canvases that don't wire
   * this up at all.
   */
  priorAnswerByBlockId?: Record<string, { status: 'loading' | 'answered' | 'unanswered'; answerOptionId?: string }>;
  /**
   * NA-319: a content-bound fingerprint of the learner's REAL,
   * actually-submitted Categorization answer per block id (never a fetched
   * or completion-status-derived guess — see decision 3b for why that
   * approach was reverted). Populated by the export runtime from live
   * submissions via `onCategorizationAnswerSubmit`, and restored from the
   * SCORM `suspend_data` backup on an actual relaunch. `CourseCanvas` only
   * trusts an entry here once it re-verifies the fingerprint still matches
   * this block's CURRENT `correctCategoryId` data (content may have been
   * edited/republished since it was recorded) — see the fingerprint-based
   * verification in `BlockWrapper` below.
   */
  categorizationFingerprintByBlockId?: Record<string, string>;
  /**
   * NA-319: fires with a Categorization block's real, actually-submitted
   * assignments the moment it genuinely completes, converted to an opaque
   * content-bound fingerprint before reaching the caller (see
   * `contentFingerprint.ts`). Only the export runtime wires this, same as
   * `onQuestionAnswer` for the other question types.
   */
  onCategorizationAnswerSubmit?: (blockId: string, fingerprint: string) => void;
  /**
   * NA-319: a content-bound fingerprint of the learner's REAL,
   * actually-checked Electronic Signature / Policy Acknowledgement state per
   * block id (neither block type has any backend persistence). Populated by
   * the export runtime from live submissions via `onAcknowledgementSubmit`,
   * and restored from the SCORM `suspend_data` backup on an actual relaunch.
   * Re-verified against the block's CURRENT document/attestation content
   * before being trusted — see `BlockWrapper` below.
   */
  acknowledgementFingerprintByBlockId?: Record<string, string>;
  /**
   * NA-319: fires the instant the learner actually checks an Electronic
   * Signature or Policy Acknowledgement checkbox, with an opaque
   * content-bound fingerprint of what was acknowledged (see
   * `contentFingerprint.ts`). Only the export runtime wires this, same as
   * `onCategorizationAnswerSubmit`.
   */
  onAcknowledgementSubmit?: (blockId: string, fingerprint: string) => void;
  /** Whether learner completion tracking is enabled for this canvas */
  enableCompletionTracking?: boolean;
  /** Whether completion celebrations are enabled for this canvas */
  enableSuccessAnimations?: boolean;
  /** Whether blocks fade in as they enter the scroll viewport */
  enableBlockReveal?: boolean;
  /** Optional custom empty-state node when there are no blocks */
  emptyState?: React.ReactNode;
  /** Optional content rendered inside the scroll area before the lesson title */
  topContent?: React.ReactNode;
  /** When true, the topContent slot uses position: sticky so it stays at the top of the scroll area as the user scrolls. Used for the lesson-level timer bar and the course-complete banner. */
  stickyTopContent?: boolean;
  /** Optional styling override */
  className?: string;
  /** Optional style object override */
  style?: React.CSSProperties;
  /** Lesson title to display in the title header */
  lessonTitle?: string;
  /** Lesson theme color for the title underline */
  lessonThemeColor?: LessonThemeColor;
  /** Content locale currently being viewed; used to pick default caption tracks. */
  currentLocale?: string;
  /**
   * NA-408 "Show Block IDs": raw numeric course id for the `C<id>` segment of
   * each block's id string — NOT `ExportedCourse.id`, which is an opaque
   * routing id (e.g. an encoded hash) and never what the id string should
   * show. Omitted (undefined) in contexts with no course, e.g. the
   * standalone Lesson Editor preview — the segment is then left out
   * entirely rather than placeholdered.
   */
  courseNumericId?: number;
  /** NA-408: raw numeric lesson id for the `L<id>` segment — see `courseNumericId`. */
  lessonNumericId?: number;
  /**
   * NA-408: locale of the text actually rendered for the current lesson —
   * distinct from `currentLocale`/the learner's selected UI locale, which can
   * differ when a lesson falls back to its default locale (incomplete
   * translation). Used for the `<LOCALE>` segment.
   */
  lessonLocale?: string;
  /** NA-408: learner has turned on "Show Block IDs" in the account popover. */
  showBlockIds?: boolean;
  /** Optional runtime transport for Centerstage transcript availability/download. */
  transcriptOperations?: VideoTranscriptOperations;
  /** 1-based lesson index within a course (for "Lesson X of Y") */
  lessonIndex?: number;
  /** Total lessons in the course */
  totalLessons?: number;
  /**
   * Fires the first time each block enters the scroll viewport (intersects
   * with the scroll root). Used by `useLearnerCourseFlow.advanceCursor` to
   * track the furthest block the learner has reached for LMS resume.
   * No completion semantics — purely a position signal.
   */
  onBlockVisible?: (blockId: string) => void;
  /**
   * Whether a host-rendered modal/overlay (privacy notice, welcome modal, …)
   * currently covers the canvas. Visibility observers gate `onBlockVisible` on
   * the block being uncovered, and IntersectionObserver never re-fires when a
   * covering element disappears — so this must be passed for observers to
   * re-evaluate blocks already on screen when the overlay closes.
   */
  overlayActive?: boolean;
  /**
   * Fires once per lesson the first time the learner engages with the content —
   * a scroll-intent (`wheel`/`touchmove`) or interaction-start (`pointerdown`/
   * `keydown`) on the scroll root. Decoupled from block completion: used to flip
   * a lesson to `in_progress` even when no block has completed yet (tall first
   * block, lesson shorter than the viewport, or an interactive-first lesson).
   */
  onEngage?: () => void;
  /**
   * If set, the canvas scrolls this block into view on the next paint and
   * then invokes `onPendingScrollSeeked`. Used by `hydrateProgress` to
   * place the learner at their last reached block on LMS rehydrate.
   */
  pendingScrollBlockId?: string | null;
  onPendingScrollSeeked?: () => void;
  /**
   * Invoked when a learner clicks the "Continue" button on a Lesson Completion
   * block in course mode. Wrapper-supplied (e.g. `flow.handleLessonCompleteContinue`)
   * to advance to the next incomplete lesson or trigger the course-completion
   * view. Ignored in standalone mode (totalLessons === 1).
   */
  onLessonCompleteContinue?: () => void;
  /**
   * Invoked when a learner clicks the "Save & Exit" button on a standalone
   * Lesson Completion block. Wrapper-supplied by SCORM/export runtimes.
   */
  onLessonCompleteSaveExit?: () => void;
  /** Optional content rendered inside the scroll area after all blocks (e.g. footer) */
  footerContent?: React.ReactNode;
  /** Optional semantic narration-event observer; defaults to the audio provider. */
  onNarrationEvent?: NarrationEventHandler;
}

const BLOCK_REVEAL_OFFSET_PX = 30;
const BLOCK_REVEAL_DURATION_MS = 600;

const BlockWrapper: React.FC<{
  pacingOwner?: BlockPacing;
  onBottomVisible?: (blockId: string) => void;
  pacedEntry?: boolean;
  pacingBoundary?: boolean;
  pacingRunning?: boolean;
  block: CourseBlock;
  onComplete: (blockId: string) => void;
  onQuestionAnswer?: (blockId: string, answer: { optionId: string }) => void;
  onFreeformTextAnswerSubmit?: (blockId: string, answer: { text: string }) => void;
  resultsByBlockId?: Record<string, BlockResultsDistributions>;
  priorAnswerByBlockId?: Record<string, { status: 'loading' | 'answered' | 'unanswered'; answerOptionId?: string }>;
  categorizationFingerprintByBlockId?: Record<string, string>;
  onCategorizationAnswerSubmit?: (blockId: string, fingerprint: string) => void;
  acknowledgementFingerprintByBlockId?: Record<string, string>;
  onAcknowledgementSubmit?: (blockId: string, fingerprint: string) => void;
  onVisible?: (blockId: string) => void;
  overlayActive?: boolean;
  isCompleted: boolean;
  /** NA-319: whether the lesson this block belongs to is itself fully completed (its Lesson Complete block has been reached). See CourseCanvas's computation for why this is a safe signal for Categorization specifically. */
  isLessonComplete: boolean;
  enableCompletionTracking: boolean;
  enableSuccessAnimations: boolean;
  enableBlockReveal: boolean;
  scrollViewport: HTMLDivElement | null;
  isStandalone: boolean;
  onLessonCompleteContinue?: () => void;
  /** Pre-resolved hex color string for the lesson theme (resolved in CourseCanvas where tokens live). */
  resolvedLessonThemeColor?: string;
  onLessonCompleteSaveExit?: () => void;
  currentLocale?: string;
  transcriptOperations?: VideoTranscriptOperations;
  onNarrationEvent?: NarrationEventHandler;
  onClickExpandNarrationTargetChange?: ClickExpandTargetRegistration;
}> = ({
  pacingOwner,
  onBottomVisible,
  pacedEntry = false,
  pacingBoundary = false,
  pacingRunning = false,
  block,
  onComplete,
  onQuestionAnswer,
  onFreeformTextAnswerSubmit,
  resultsByBlockId,
  priorAnswerByBlockId,
  categorizationFingerprintByBlockId,
  onCategorizationAnswerSubmit,
  acknowledgementFingerprintByBlockId,
  onAcknowledgementSubmit,
  onVisible,
  overlayActive,
  isCompleted,
  isLessonComplete,
  enableCompletionTracking,
  enableSuccessAnimations,
  enableBlockReveal,
  scrollViewport,
  isStandalone,
  onLessonCompleteContinue,
  resolvedLessonThemeColor,
  onLessonCompleteSaveExit,
  currentLocale,
  transcriptOperations,
  onNarrationEvent,
  onClickExpandNarrationTargetChange,
}) => {
  const { enforceFullVideoView } = useAccountConfig();
  const reducedMotion = usePrefersReducedMotion();
  const effectiveCompletionMode = getEffectiveBlockCompletionMode(block, enforceFullVideoView);
  const videoRef = useRef<MediaPlayerInstance>(null);
  const blockRef = useRef<HTMLDivElement>(null);
  const { isRevealed, disableTransition } = useBlockReveal(blockRef, scrollViewport, undefined, pacedEntry);

  const handleComplete = useCallback(
    (blockId: string) => {
      const successAnimationType = resolveSuccessAnimationType(block);
      if (enableSuccessAnimations && !isCompleted && successAnimationType) {
        triggerSuccessAnimation(successAnimationType);
      }
      onComplete(blockId);
    },
    [block, enableSuccessAnimations, isCompleted, onComplete]
  );

  useScrollCompletion(
    block.id,
    blockRef,
    handleComplete,
    Boolean(onBottomVisible) || (enableCompletionTracking && effectiveCompletionMode === 'scroll' && (!enableBlockReveal || isRevealed)),
    scrollViewport,
    onVisible,
    overlayActive,
    { onBottomVisible, hasBoundary: pacingBoundary, resetKey: pacingOwner, completionEnabled: enableCompletionTracking && effectiveCompletionMode === 'scroll' }
  );

  // NA-319: Categorization has no backend persistence, so its
  // relaunch-restore signal is a content-bound fingerprint (see
  // `contentFingerprint.ts`) rather than the raw answer — computed here
  // once so both the verify check and the fallback derivation below reuse
  // the same reconstruction instead of recomputing it three times.
  const categorizationCorrectAssignments = block.type === 'categorization'
    ? correctCategorizationAssignments((block as CategorizationBlock).items)
    : undefined;
  // True only when a restored fingerprint still matches this block's
  // CURRENT correct-category data — a stale/mismatched fingerprint (content
  // edited/republished since it was recorded) must NOT block the
  // lesson-completion fallback below, so this is combined with `||`, not
  // `??`, at the call site.
  const categorizationFingerprintVerified =
    categorizationCorrectAssignments !== undefined
    && categorizationFingerprintByBlockId?.[block.id] !== undefined
    && categorizationFingerprintByBlockId[block.id]
      === fingerprintCategorizationAnswer(categorizationCorrectAssignments);

  // Electronic Signature / Policy Acknowledgement / Link (NA-409) have no
  // backend persistence either. Unlike Categorization (which unconditionally
  // gates lesson progression), these block types only gate when the AUTHOR
  // turned on `requireInteraction` for this specific instance —
  // `effectiveCompletionMode === 'interaction'` is exactly the same check
  // the reached-cursor/gating logic itself uses (see
  // `isBlockingBlock`/`blockRequiresCompletion`) — so the free
  // lesson-completion fallback below is only trusted when this instance
  // genuinely gates; otherwise a completed lesson proves nothing about
  // whether this specific checkbox was ever checked (or link clicked), and
  // the fingerprint restore (recorded at the moment the action actually
  // happened, regardless of gating) is the only safe signal.
  const isAcknowledgementBlock = block.type === 'electronic-signature' || block.type === 'policy-acknowledgement' || block.type === 'link';
  const acknowledgementFingerprintVerified =
    isAcknowledgementBlock
    && acknowledgementFingerprintByBlockId?.[block.id] !== undefined
    && acknowledgementFingerprintByBlockId[block.id]
      === fingerprintAcknowledgementContent(block as ElectronicSignatureBlock | PolicyAcknowledgementBlock | LinkBlock);

  const animateReveal = enableBlockReveal || pacedEntry;
  const shouldShowBlock = !animateReveal || isRevealed || reducedMotion;

  const animationStyle: React.CSSProperties = {
    opacity: shouldShowBlock ? 1 : 0,
    transform: shouldShowBlock ? 'translateY(0)' : `translateY(${BLOCK_REVEAL_OFFSET_PX}px)`,
    transition: !animateReveal || disableTransition || reducedMotion
      ? 'none'
      : `opacity ${BLOCK_REVEAL_DURATION_MS}ms ease-out, transform ${BLOCK_REVEAL_DURATION_MS}ms ease-out`,
    width: '100%',
  };

  return (
    <div
      ref={blockRef}
      className="course-block"
      data-block-id={block.id}
      data-block-type={block.type}
      style={animationStyle}
    >
      <InteractionMessageCompletionProvider value={isCompleted}>
        {renderCourseCanvasBlock({
          block,
          videoRef,
          onInteractionComplete: enableCompletionTracking && effectiveCompletionMode === 'interaction'
            ? () => handleComplete(block.id)
            : undefined,
          onAnswerSubmit: onQuestionAnswer
            ? (answer) => onQuestionAnswer(block.id, answer)
            : undefined,
          onFreeformTextAnswerSubmit: onFreeformTextAnswerSubmit
            ? (answer) => onFreeformTextAnswerSubmit(block.id, answer)
            : undefined,
          resultsDistributions: resultsByBlockId?.[block.id],
          // NA-319: a completion-status-derived fallback was tried here for
          // Categorization and gated Single Choice, then reverted after
          // real-device testing confirmed it could show a WRONG answer as
          // CORRECT — `isCompleted` is reconstructed from the player's
          // "furthest reached" cursor, not a verified completion record, so
          // it isn't a safe signal to override the fetched answer with. Back
          // to the plain fetched/persisted value only for gated Single
          // Choice: it locks only when this value already equals the
          // correct option.
          priorAnswerStatus: priorAnswerByBlockId?.[block.id]?.status,
          priorAnswerOptionId: priorAnswerByBlockId?.[block.id]?.answerOptionId,
          // NA-319: trust the restored fingerprint (verified above against
          // this block's CURRENT correct-category data) OR the
          // lesson-completion fallback — `||`, not `??`, so a stale/
          // mismatched fingerprint falls through to the fallback instead of
          // silently blocking it.
          categorizationInitialCorrectAssignments:
            categorizationFingerprintVerified || (isLessonComplete && block.type === 'categorization')
              ? categorizationCorrectAssignments
              : undefined,
          onCategorizationAnswerSubmit: onCategorizationAnswerSubmit
            ? (assignments) => onCategorizationAnswerSubmit(block.id, fingerprintCategorizationAnswer(assignments))
            : undefined,
          // NA-319: same reasoning as Categorization above — trust the
          // restored fingerprint (verified against this block's CURRENT
          // document/attestation content) OR the lesson-completion fallback,
          // combined with `||` so one doesn't block the other.
          acknowledgementInitialAcknowledged:
            acknowledgementFingerprintVerified
            || (isAcknowledgementBlock && isLessonComplete && effectiveCompletionMode === 'interaction')
              ? true
              : undefined,
          onAcknowledgementSubmit: onAcknowledgementSubmit
            ? () => onAcknowledgementSubmit(
                block.id,
                fingerprintAcknowledgementContent(block as ElectronicSignatureBlock | PolicyAcknowledgementBlock | LinkBlock)
              )
            : undefined,
          isStandalone,
          onLessonCompleteContinue,
          onLessonCompleteSaveExit,
          lessonThemeColor: resolvedLessonThemeColor,
          currentLocale,
          transcriptOperations,
          onNarrationEvent,
          onClickExpandNarrationTargetChange,
        })}
      </InteractionMessageCompletionProvider>
      {pacingBoundary && <PacingBoundary running={pacingRunning} />}
    </div>
  );
};

// Renders the AudioControls cluster for whichever block is currently the
// "current" block per the tracking pass below. Passed to CourseSectionShell's
// section-overlay slot, which pins it near the viewport top for as long as the
// block's section spans the viewport — the cluster stays owned by its block
// rather than becoming a globally floating player.
const CurrentBlockAudioSlot: React.FC<{ blockId: string }> = ({ blockId }) => {
  const { audioOn, currentBlockId } = useAudioControls();
  if (!audioOn || currentBlockId !== blockId) return null;
  return <AudioControls />;
};

/**
 * NA-408 "Show Block IDs": plain selectable text, no pill/border/icon/hover/
 * cursor treatment. Passed to `CourseSectionShell`'s `bottomOverlay` slot,
 * which positions it low in the section's own bottom padding without adding
 * height or shifting the block.
 */
const BlockIdLabel: React.FC<{ text: string }> = ({ text }) => {
  const { tokens } = useTheme();
  return (
    <span
      style={{
        display: 'block',
        color: tokens.text.muted,
        fontSize: tokens.typography.sizes.xs,
        fontWeight: tokens.typography.weights.regular,
        lineHeight: tokens.typography.lineHeights.uiSoft,
        letterSpacing: '0.02em',
        fontVariantNumeric: 'tabular-nums',
      }}
    >
      {text}
    </span>
  );
};

function isBlockingBlock(
  block: CourseBlock,
  completionStatus: Record<string, boolean>,
  enforceFullVideoView: boolean
): boolean {
  if (!blockRequiresCompletion(block, enforceFullVideoView)) {
    return false;
  }
  return completionStatus[block.id] !== true;
}

function calculateVisibleBlocks(
  blocks: CourseBlock[],
  completionStatus: Record<string, boolean>,
  enforceFullVideoView: boolean
): { visibleBlocks: CourseBlock[]; blockingIndex: number | null } {
  let blockingIndex: number | null = null;

  for (let i = 0; i < blocks.length; i++) {
    const block = blocks[i];
    if (block && isBlockingBlock(block, completionStatus, enforceFullVideoView)) {
      blockingIndex = i;
      break;
    }
  }

  if (blockingIndex !== null) {
    return {
      visibleBlocks: blocks.slice(0, blockingIndex + 1),
      blockingIndex,
    };
  }

  return {
    visibleBlocks: blocks,
    blockingIndex: null,
  };
}

export const CourseCanvas: React.FC<CourseCanvasProps> = ({
  pacing,
  blocks,
  completionStatus,
  onBlockComplete,
  onQuestionAnswer,
  onFreeformTextAnswerSubmit,
  resultsByBlockId,
  priorAnswerByBlockId,
  categorizationFingerprintByBlockId,
  onCategorizationAnswerSubmit,
  acknowledgementFingerprintByBlockId,
  onAcknowledgementSubmit,
  enableCompletionTracking = true,
  enableSuccessAnimations = false,
  enableBlockReveal = true,
  emptyState,
  topContent,
  stickyTopContent = false,
  className,
  style,
  lessonTitle,
  lessonThemeColor,
  currentLocale,
  courseNumericId,
  lessonNumericId,
  lessonLocale,
  showBlockIds = false,
  lessonIndex,
  totalLessons,
  onBlockVisible,
  overlayActive,
  onEngage,
  pendingScrollBlockId,
  onPendingScrollSeeked,
  onLessonCompleteContinue,
  onLessonCompleteSaveExit,
  footerContent,
  transcriptOperations,
  onNarrationEvent,
}) => {
  const { tokens } = useTheme();
  // Account-enforced full-video viewing makes a video block gate progression
  // even when its author left interaction off, so the reveal gate must account
  // for it too (not just the toolbar Next button) — otherwise the learner
  // scrolls straight past an enforced video.
  const { enforceFullVideoView } = useAccountConfig();
  const resolvedLessonThemeColor = resolveLessonThemeColor(lessonThemeColor, tokens.lessonColors);
  // Callback ref instead of useRef + useLayoutEffect: on a fresh load the lesson
  // data arrives async, so this canvas first mounts with the empty state (the
  // scroll-root div is not rendered yet). A `useLayoutEffect([], ...)` would
  // capture `null` once and never re-run, leaving every block's reveal hook
  // without a viewport (permanently stuck at opacity:0). A callback ref fires
  // whenever the node attaches — including when the real scroll-root mounts
  // after blocks load — so the viewport is always captured.
  const [contentElement, setContentElement] = React.useState<HTMLDivElement | null>(null);
  const setContentRef = React.useCallback((node: HTMLDivElement | null) => {
    setContentElement(node);
  }, []);

  const { visibleBlocks: interactionVisibleBlocks, blockingIndex } = React.useMemo(
    () => calculateVisibleBlocks(blocks, completionStatus, enforceFullVideoView),
    [blocks, completionStatus, enforceFullVideoView]
  );

  // NA-319: whether the CURRENT lesson (the one these `blocks` belong to) is
  // itself fully completed — true once its own Lesson Complete block has
  // been reached. Unlike a single required block's own `completionStatus`
  // (which the reverted Categorization/gated-Single-Choice fallback relied
  // on unsafely — see design.md decision 3b), this is a materially safer
  // signal: Categorization always gates progression (no author toggle to
  // make it skippable), so the Lesson Complete block can never render/be
  // reached while a Categorization question in the same lesson is still
  // wrong or unanswered, and the percent-to-completionStatus reconstruction
  // has its own dedicated guard against crossing a Lesson Complete boundary
  // it hasn't unambiguously earned. Used below to show a Categorization
  // question as fully correct once its lesson is confirmed complete, as a
  // fallback when no real same-session submission is already known.
  const lessonCompleteBlockId = React.useMemo(
    () => blocks.find((b) => isLessonCompleteBlock(b))?.id,
    [blocks]
  );
  const isLessonComplete = lessonCompleteBlockId !== undefined && completionStatus[lessonCompleteBlockId] === true;
  const pacingRevision = React.useSyncExternalStore(
    pacing?.subscribe ?? emptyPacingSubscribe,
    pacing?.getSnapshot ?? emptyPacingSnapshot,
    emptyPacingSnapshot,
  );
  const visibleBlocks = React.useMemo(() => {
    if (!pacing || isLessonComplete || pacing.getMode() === 'off') return interactionVisibleBlocks;
    return interactionVisibleBlocks.slice(0, pacedBlockCount(blocks.map((block) => block.id), pacing));
  }, [pacing, pacingRevision, blocks, interactionVisibleBlocks, isLessonComplete]);
  useNarrationPacing({ pacing, blocks, completed: isLessonComplete, overlayActive, root: contentElement, enforceFullVideoView });
  const frontier = visibleBlocks[visibleBlocks.length - 1];
  const hasPacingBoundary = Boolean(
    pacing
      && pacing.getMode() !== 'off'
      && !isLessonComplete
      && visibleBlocks.length < blocks.length
      && frontier
      && !isLessonCompleteBlock(frontier),
  );
  const clearBottom = React.useCallback((id: string) => {
    const block = blocks.find((candidate) => candidate.id === id);
    if (block && !isLessonCompleteBlock(block) && !blockRequiresCompletion(block, enforceFullVideoView)) pacing?.clear(id);
  }, [blocks, enforceFullVideoView, pacing]);
  React.useEffect(() => {
    const reconcileRequired = (): void => {
      pacing?.reconcile();
      if (overlayActive || document.visibilityState === 'hidden' || !hasPacingBoundary || !frontier) return;
      if (blockRequiresCompletion(frontier, enforceFullVideoView) && completionStatus[frontier.id]) {
        pacing?.clear(frontier.id);
      }
    };
    reconcileRequired();
    document.addEventListener('visibilitychange', reconcileRequired);
    return () => document.removeEventListener('visibilitychange', reconcileRequired);
  }, [pacing, pacingRevision, frontier, hasPacingBoundary, completionStatus, enforceFullVideoView, overlayActive]);


  // The pinned narration controls have to clear the sticky top banner, whose
  // height depends on its content (timer bar, completion banner) and the
  // viewport width — so it is measured rather than assumed.
  const [stickyBandElement, setStickyBandElement] = React.useState<HTMLDivElement | null>(null);
  const stickyBandHeight = useMeasuredHeight(stickyBandElement);

  const {
    currentBlockId,
    setCurrentBlockId,
    handleNarrationEvent: handleProviderNarrationEvent,
    registerClickExpandTarget,
  } = useAudioControls();
  const currentBlockIdRef = React.useRef(currentBlockId);
  currentBlockIdRef.current = currentBlockId;
  const routeNarrationEvent = React.useCallback<NarrationEventHandler>((event) => {
    handleProviderNarrationEvent(event);
    onNarrationEvent?.(event);
  }, [handleProviderNarrationEvent, onNarrationEvent]);

  // Single IntersectionObserver tracks which blocks have entered the viewport
  // so the learner-flow cursor can advance for LMS progress reporting.
  // Decoupled from completion semantics — every block contributes.
  const onBlockVisibleRef = React.useRef(onBlockVisible);
  onBlockVisibleRef.current = onBlockVisible;
  React.useEffect(() => {
    if (!contentElement) return;
    if (typeof IntersectionObserver === 'undefined') return;

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!isVisibleIntersectionUncovered(entry)) continue;
          const id = (entry.target as HTMLElement).dataset.blockId;
          if (!id) continue;
          onBlockVisibleRef.current?.(id);
        }
      },
      { root: contentElement, threshold: 0.01 }
    );

    const blockEls = contentElement.querySelectorAll<HTMLElement>('[data-block-id]');
    blockEls.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, [contentElement, visibleBlocks, overlayActive]);

  // Drives the "current block" signal for the NA-165 audio narration cluster.
  // Rule (Sara): "Current" = the block whose first line of text is currently
  // visible on screen and closest to the top. If first line has scrolled past
  // (or not yet entered the viewport), keep the most recent / next nearest block
  // so the cluster doesn't blank between blocks.
  // "First line of text" is approximated as the first heading/paragraph inside
  // each block — for media-on-top blocks the title sits below the image, so
  // using BlockWrapper.top would over-eagerly mark the block current.
  const setCurrentBlockIdRef = React.useRef(setCurrentBlockId);
  setCurrentBlockIdRef.current = setCurrentBlockId;
  React.useEffect(() => {
    if (!contentElement) return;

    let lastCurrent: string | null = null;
    let rafHandle: number | null = null;
    let manualIntent = false;
    let intentTimeout: ReturnType<typeof setTimeout> | undefined;
    const clearIntent = (): void => { manualIntent = false; clearTimeout(intentTimeout); };
    const retainIntent = (): void => {
      clearTimeout(intentTimeout);
      // Keep keyboard smooth scrolling and touch inertia attached to the gesture.
      // scrollend clears immediately where supported; this covers older players.
      intentTimeout = setTimeout(clearIntent, 200);
    };
    const detachIntent = attachManualIntentListeners((event) => {
      // A topic press in the current Click-Expand block is narration's own topic
      // step. The panel reflow it causes must not read as moving to another block.
      const current = currentBlockIdRef.current;
      if (current !== null && isClickExpandTopicControlOf(event.target, current)) return;
      manualIntent = true;
      retainIntent();
    });

    const recomputeCurrent = (): void => {
      rafHandle = null;
      const blockEls = contentElement.querySelectorAll<HTMLElement>('[data-block-id]');
      if (blockEls.length === 0) {
        if (lastCurrent !== null) {
          lastCurrent = null;
          setCurrentBlockIdRef.current?.(null);
        }
        return;
      }
      const rootRect = contentElement.getBoundingClientRect();
      // Visibility is measured from the effective viewport top — below a
      // sticky band when one covers it — so content hidden behind the band is
      // never treated as current. The range still ends at the viewport's real
      // bottom edge, hence the matching height reduction.
      const stickyBandHeight = measureStickyTopBandHeight(contentElement);
      const rootTop = rootRect.top + stickyBandHeight;
      const viewportHeight = rootRect.height - stickyBandHeight;

      let visibleBestId: string | null = null;
      let visibleBestY = Infinity;
      let aboveBestId: string | null = null;
      let aboveBestY = -Infinity;
      let belowBestId: string | null = null;
      let belowBestY = Infinity;

      blockEls.forEach((el) => {
        const id = el.dataset.blockId;
        if (!id) return;
        const textEl = el.querySelector<HTMLElement>('h1, h2, h3, h4, h5, h6, p');
        const refEl = textEl ?? el;
        const y = refEl.getBoundingClientRect().top - rootTop;

        if (y >= 0 && y <= viewportHeight) {
          if (y < visibleBestY) {
            visibleBestY = y;
            visibleBestId = id;
          }
        } else if (y < 0) {
          if (y > aboveBestY) {
            aboveBestY = y;
            aboveBestId = id;
          }
        } else if (y < belowBestY) {
          belowBestY = y;
          belowBestId = id;
        }
      });

      const bestId = visibleBestId ?? aboveBestId ?? belowBestId;
      if (bestId !== lastCurrent || manualIntent) {
        lastCurrent = bestId;
        setCurrentBlockIdRef.current?.(bestId, manualIntent ? 'manual' : 'passive');
      }
    };

    const scheduleRecompute = (): void => {
      if (rafHandle !== null) return;
      rafHandle = window.requestAnimationFrame(recomputeCurrent);
    };

    scheduleRecompute();
    const handleScroll = (): void => {
      if (manualIntent) retainIntent();
      scheduleRecompute();
    };
    const handleFocus = (event: FocusEvent): void => {
      const id = event.target instanceof Element ? event.target.closest<HTMLElement>('[data-block-id]')?.dataset.blockId : undefined;
      if (id) setCurrentBlockIdRef.current?.(id, 'manual');
    };
    contentElement.addEventListener(NARRATION_SCROLL_START_EVENT, clearIntent);
    contentElement.addEventListener('focusin', handleFocus);
    contentElement.addEventListener('scroll', handleScroll, { passive: true });
    contentElement.addEventListener('scrollend', clearIntent);
    window.addEventListener('resize', scheduleRecompute);

    return () => {
      if (rafHandle !== null) window.cancelAnimationFrame(rafHandle);
      detachIntent();
      clearIntent();
      contentElement.removeEventListener(NARRATION_SCROLL_START_EVENT, clearIntent);
      contentElement.removeEventListener('focusin', handleFocus);
      contentElement.removeEventListener('scroll', handleScroll);
      contentElement.removeEventListener('scrollend', clearIntent);
      window.removeEventListener('resize', scheduleRecompute);
    };
  }, [contentElement, visibleBlocks]);

  // First-engagement signal: fire `onEngage` once per lesson on the first
  // user scroll-intent or interaction-start. Listens for `wheel`/`touchmove`/
  // `pointerdown`/`keydown` rather than `scroll`, so programmatic scroll
  // restoration (which dispatches `scroll`) never counts as engagement.
  const onEngageRef = React.useRef(onEngage);
  onEngageRef.current = onEngage;
  React.useEffect(() => {
    if (!contentElement) return;

    let engaged = false;
    const handleEngage = () => {
      if (engaged) return;
      engaged = true;
      onEngageRef.current?.();
      detach();
    };
    const detach = () => {
      contentElement.removeEventListener('wheel', handleEngage);
      contentElement.removeEventListener('touchmove', handleEngage);
      contentElement.removeEventListener('pointerdown', handleEngage);
      contentElement.removeEventListener('keydown', handleEngage);
    };

    contentElement.addEventListener('wheel', handleEngage, { passive: true });
    contentElement.addEventListener('touchmove', handleEngage, { passive: true });
    contentElement.addEventListener('pointerdown', handleEngage, { passive: true });
    contentElement.addEventListener('keydown', handleEngage);

    return detach;
    // `blocks` in deps re-arms the signal when the lesson changes.
  }, [contentElement, blocks]);

  // Block-level seek on hydrate: scroll the requested block into the
  // viewport, then notify the consumer so the request isn't replayed.
  // Only clear after a successful seek — otherwise the request rides along
  // until `visibleBlocks` updates with the cursor's lesson rendered.
  React.useLayoutEffect(() => {
    if (!pendingScrollBlockId || !contentElement) return;
    const el = contentElement.querySelector<HTMLElement>(
      `[data-block-id="${cssEscape(pendingScrollBlockId)}"]`
    );
    if (!el) return;
    el.scrollIntoView({ block: 'start', behavior: 'auto' });
    onPendingScrollSeeked?.();
  }, [pendingScrollBlockId, contentElement, onPendingScrollSeeked, visibleBlocks]);

  const styles = React.useMemo<Record<string, React.CSSProperties>>(
    () => ({
      canvas: {
        flex: 1,
        height: '100%',
        backgroundColor: tokens.backgrounds.courseShell,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      },
      content: {
        flex: 1,
        overflow: 'auto',
        overflowAnchor: 'none',
        backgroundColor: tokens.backgrounds.page,
      },
      topContent: {
        // Figma 3987:707 — 50px of breathing room above and below the completion banner.
        padding: '50px 0',
        boxSizing: 'border-box',
        backgroundColor: tokens.backgrounds.page,
      },
      topContentSticky: {
        position: 'sticky',
        top: 0,
        zIndex: 10,
        padding: '50px 0',
        boxSizing: 'border-box',
      },
      // Absolutely-positioned sibling behind the band content, not a property
      // on the sticky wrapper itself — a mask-image there would clip any
      // descendant (e.g. the timer bar's `?` tooltip) that extends past the
      // wrapper's edges.
      topContentStickyOverlay: {
        position: 'absolute',
        inset: 0,
        pointerEvents: 'none',
        backgroundImage: `linear-gradient(to bottom, rgba(255, 255, 255, 0.95) 0, rgba(255, 255, 255, 0.95) 50px, rgba(255, 255, 255, 0.85) 50px, rgba(255, 255, 255, 0.85) calc(100% - 40px), transparent 100%)`,
        maskImage: 'linear-gradient(to right, transparent 0%, black 10%, black 90%, transparent 100%)',
        WebkitMaskImage: 'linear-gradient(to right, transparent 0%, black 10%, black 90%, transparent 100%)',
      },
      topContentStickyBand: {
        position: 'relative',
      },
      // Banner is constrained to 80% of the content area, centered — so its left edge
      // lands at the same 10% offset as the title-block content (LessonTitleHeader grid).
      topContentBand: {
        width: '80%',
        maxWidth: `calc(${tokens.layout.contentMaxWidth} * 0.8)`,
        margin: '0 auto',
      },
      sectionContent: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: tokens.spacing.xl,
      },
      emptyState: {
        flex: 1,
        minHeight: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: tokens.spacing["5xl"],
        textAlign: 'center',
        color: tokens.text.primary,
        backgroundColor: tokens.backgrounds.page,
      },
      emptyIcon: {
        fontSize: tokens.typography.mobile.h1.size,
        marginBottom: tokens.spacing.lg,
      },
      emptyTitle: {
        margin: `0 0 ${tokens.spacing.md}`,
        fontSize: tokens.typography.mobile.h3.size,
        fontWeight: tokens.typography.mobile.h3.weight,
        lineHeight: tokens.typography.mobile.h3.lineHeight,
        color: tokens.text.primary,
      },
      emptyText: {
        margin: 0,
        fontSize: tokens.typography.mobile.body.size,
        fontWeight: tokens.typography.mobile.body.weight,
        lineHeight: tokens.typography.mobile.body.lineHeight,
        color: tokens.text.muted,
      },
    }),
    [tokens]
  );

  if (blocks.length === 0) {
    if (emptyState !== undefined) {
      return <>{emptyState}</>;
    }

    return (
      <div style={{ ...styles.canvas, ...style }} className={className}>
        <div style={styles.emptyState}>
          <div style={styles.emptyIcon}>👁️</div>
          <h2 style={styles.emptyTitle}>Course Content</h2>
          <p style={styles.emptyText}>
            This lesson has no content yet.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ ...styles.canvas, ...style }} className={className} data-testid="course-canvas">
      <div
        ref={setContentRef}
        style={styles.content}
        data-testid="course-canvas-scroll-root"
        data-overlay-active={overlayActive ? "true" : "false"}
      >
        {topContent ? (
          <div
            ref={stickyTopContent ? setStickyBandElement : undefined}
            style={stickyTopContent ? styles.topContentSticky : styles.topContent}
            data-testid="course-canvas-top-content"
            // Sticky-only marker: narration scroll anchoring and current-block
            // tracking offset for a band that covers the viewport top, never
            // for top content that scrolls away.
            data-sticky-top-band={stickyTopContent ? 'true' : undefined}
          >
            {stickyTopContent ? (
              <div style={styles.topContentStickyOverlay} aria-hidden="true" />
            ) : null}
            <div
              style={
                stickyTopContent
                  ? { ...styles.topContentBand, ...styles.topContentStickyBand }
                  : styles.topContentBand
              }
            >
              {topContent}
            </div>
          </div>
        ) : null}
        {lessonTitle && (
          <LessonTitleHeader
            title={lessonTitle}
            themeColor={lessonThemeColor}
            lessonIndex={lessonIndex}
            totalLessons={totalLessons}
            hasContentAbove={Boolean(topContent)}
          />
        )}
        {visibleBlocks.map((block, index) => (
          <CourseSectionShell
            key={block.id}
            index={index}
            innerStyle={styles.sectionContent}
            stickyOverlay={<CurrentBlockAudioSlot blockId={block.id} />}
            stickyOverlayOffset={stickyBandHeight}
            bottomOverlay={
              showBlockIds && lessonNumericId != null && lessonLocale ? (
                <BlockIdLabel
                  text={formatBlockId({
                    courseId: courseNumericId,
                    lessonId: lessonNumericId,
                    blockId: block.id,
                    locale: lessonLocale,
                    translationType: block.translationType,
                  })}
                />
              ) : undefined
            }
          >
            <BlockWrapper
              block={block}
              pacingOwner={pacing}
              onBottomVisible={
                pacing?.getMode() === 'positive'
                  && hasPacingBoundary
                  && block.id === frontier?.id
                  && !blockRequiresCompletion(block, enforceFullVideoView)
                  ? clearBottom
                  : undefined
              }
              pacingBoundary={Boolean(pacing && pacing.getMode() !== 'off' && !isLessonCompleteBlock(block) && index < blocks.length - 1)}
              pacingRunning={Boolean(pacing?.getMode() === 'positive' && !isLessonComplete && pacing.deadline(block.id))}
              pacedEntry={Boolean(pacing?.getMode() === 'positive' && !isLessonComplete && index > 0 && pacing.wasPaced(blocks[index - 1]?.id ?? '') && !pendingScrollBlockId)}
              onComplete={onBlockComplete}
              onQuestionAnswer={onQuestionAnswer}
              onFreeformTextAnswerSubmit={onFreeformTextAnswerSubmit}
              resultsByBlockId={resultsByBlockId}
              priorAnswerByBlockId={priorAnswerByBlockId}
              categorizationFingerprintByBlockId={categorizationFingerprintByBlockId}
              onCategorizationAnswerSubmit={onCategorizationAnswerSubmit}
              acknowledgementFingerprintByBlockId={acknowledgementFingerprintByBlockId}
              onAcknowledgementSubmit={onAcknowledgementSubmit}
              onVisible={onBlockVisible}
              overlayActive={overlayActive}
              isCompleted={completionStatus[block.id] === true}
              isLessonComplete={isLessonComplete}
              enableCompletionTracking={enableCompletionTracking}
              enableSuccessAnimations={enableSuccessAnimations}
              enableBlockReveal={enableBlockReveal}
              scrollViewport={contentElement}
              isStandalone={(totalLessons ?? 1) === 1}
              onLessonCompleteContinue={onLessonCompleteContinue}
              resolvedLessonThemeColor={resolvedLessonThemeColor}
              onLessonCompleteSaveExit={onLessonCompleteSaveExit}
              currentLocale={currentLocale}
              transcriptOperations={transcriptOperations}
              onNarrationEvent={routeNarrationEvent}
              onClickExpandNarrationTargetChange={registerClickExpandTarget}
            />
          </CourseSectionShell>
        ))}
        {blockingIndex === null && !hasPacingBoundary && footerContent}
      </div>
    </div>
  );
};
